import { useState, useEffect, useCallback, useMemo } from "react";
import {
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Checkbox,
} from "@mui/material";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

import BaseDialog from "../ui/BaseDialog";
import ActionButton from "../ui/ActionButton";
import Alert from "../ui/Alert";
import config from "../../config";
import { isMasterAdmin } from "../../utils/auth";
import { exportDonorReportPpt } from "../../pages/Donor/GHG/components/exportDonorReportPpt";

const toDateStr = (d) => (d ? d.toISOString().split("T")[0] : "");

const getPresetRange = (preset) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const quarter = Math.floor(month / 3);

  switch (preset) {
    case "thisMonth":
      return { start: new Date(year, month, 1), end: new Date(year, month + 1, 0) };
    case "lastMonth": {
      const y = month === 0 ? year - 1 : year;
      const m = month === 0 ? 11 : month - 1;
      return { start: new Date(y, m, 1), end: new Date(y, m + 1, 0) };
    }
    case "thisQuarter":
      return { start: new Date(year, quarter * 3, 1), end: new Date(year, quarter * 3 + 3, 0) };
    case "thisYear":
      return { start: new Date(year, 0, 1), end: new Date(year, 11, 31) };
    case "lastYear":
      return { start: new Date(year - 1, 0, 1), end: new Date(year - 1, 11, 31) };
    case "allTime":
      return { start: null, end: null };
    default:
      return { start: null, end: null };
  }
};

const PRESETS = [
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
  { key: "thisQuarter", label: "This Quarter" },
  { key: "thisYear", label: "This Year" },
  { key: "lastYear", label: "Last Year" },
  { key: "allTime", label: "All Time" },
];

const DEFAULT_PRESET = "thisYear";

export default function CreateReportModal({
  open,
  onClose,
  darkMode,
  initialDateFrom = "",
  initialDateTo = "",
  receivers = [],
  companyName = "",
}) {
  const { t } = useTranslation();

  // default to "This Year" preset
  const defaultRange = getPresetRange(DEFAULT_PRESET);
  const [activePreset, setActivePreset] = useState(DEFAULT_PRESET);
  const [dateFrom, setDateFrom] = useState(toDateStr(defaultRange.start));
  const [dateTo, setDateTo] = useState(toDateStr(defaultRange.end));
  const [scope, setScope] = useState("all");
  const [receiverId, setReceiverId] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");

  // section toggles for PPT export
  const [sections, setSections] = useState({
    trendChart: true,
    trendTable: true,
    materialChart: true,
    materialTable: true,
    receiverTable: true,
  });

  const toggleSection = useCallback((key) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // deduplicate receivers by name as safety net
  const uniqueReceivers = useMemo(() => {
    const seen = new Set();
    return (receivers || []).filter((r) => {
      const key = (r.name || "").trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [receivers]);

  // reset form when modal opens
  useEffect(() => {
    if (open) {
      const range = getPresetRange(DEFAULT_PRESET);
      setActivePreset(DEFAULT_PRESET);
      setDateFrom(toDateStr(range.start));
      setDateTo(toDateStr(range.end));
      setScope("all");
      setReceiverId("");
      setError("");
      setSections({
        trendChart: true,
        trendTable: true,
        materialChart: true,
        materialTable: true,
        receiverTable: true,
      });
    }
  }, [open]);

  const handlePreset = useCallback((key) => {
    setActivePreset(key);
    const range = getPresetRange(key);
    setDateFrom(toDateStr(range.start));
    setDateTo(toDateStr(range.end));
  }, []);

  const handleDateChange = useCallback((field, value) => {
    setActivePreset("custom");
    if (field === "from") setDateFrom(value);
    else setDateTo(value);
  }, []);

  const handleClose = useCallback(() => {
    if (isGenerating) return;
    setError("");
    onClose();
  }, [isGenerating, onClose]);

  const handleGenerate = useCallback(async () => {
    setError("");

    if (scope === "specific" && !receiverId) {
      setError(t("Please select a receiver."));
      return;
    }

    setIsGenerating(true);

    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (scope === "specific" && receiverId) params.append("receiver_id", receiverId);
      if (isMasterAdmin()) params.append("show_all", "true");

      const res = await fetch(`${config.apiUrl}/donor/ghg/report-data?${params}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        credentials: "include",
      });

      const result = await res.json();

      if (!res.ok || !result.success) {
        setError(result.message || t("Failed to fetch report data."));
        return;
      }

      if (!result.materialBreakdown?.length && !result.receiverBreakdown?.length) {
        setError(t("No donation data found for the selected range."));
        return;
      }

      await exportDonorReportPpt({
        companyName,
        dateFrom,
        dateTo,
        darkMode,
        summary: result.summary,
        materialBreakdown: result.materialBreakdown,
        receiverBreakdown: result.receiverBreakdown,
        trend: result.trend,
        isPerReceiver: scope === "specific",
        receiverName: result.receiverName || uniqueReceivers.find((r) => String(r.id) === String(receiverId))?.name || "",
        sections,
      });

      onClose();
    } catch (err) {
      console.error("Report generation failed:", err);
      setError(t("Something went wrong. Please try again."));
    } finally {
      setIsGenerating(false);
    }
  }, [dateFrom, dateTo, scope, receiverId, uniqueReceivers, companyName, darkMode, sections, onClose, t]);

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      darkMode={darkMode}
      title={t("Create Report")}
      showCloseButton
      actions={
        <>
          <ActionButton variant="default" modal onClick={handleClose} disabled={isGenerating}>
            {t("Cancel")}
          </ActionButton>
          <ActionButton
            variant="secondary"
            modal
            icon={faFileExport}
            onClick={handleGenerate}
            loading={isGenerating}
          >
            {isGenerating ? t("Generating...") : t("Generate")}
          </ActionButton>
        </>
      }
    >
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Alert message={error} severity="error" />
        </div>
      )}

      {/* period presets */}
      <div style={{ marginBottom: 16 }}>
        <label
          style={{
            display: "block",
            marginBottom: 8,
            fontWeight: 500,
            fontFamily: "Poppins, sans-serif",
            fontSize: "0.85rem",
            color: "var(--text-primary)",
          }}
        >
          {t("Period")}
        </label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              className={`ghg-preset-btn ${activePreset === p.key ? "active" : ""}`}
              onClick={() => handlePreset(p.key)}
              disabled={isGenerating}
              type="button"
            >
              {t(p.label)}
            </button>
          ))}
        </div>
      </div>

      {/* custom date range */}
      <div style={{ marginBottom: 16 }}>
        <label
          style={{
            display: "block",
            marginBottom: 8,
            fontWeight: 500,
            fontFamily: "Poppins, sans-serif",
            fontSize: "0.85rem",
            color: "var(--text-primary)",
          }}
        >
          {t("Date Range")}
        </label>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => handleDateChange("from", e.target.value)}
            inputProps={{ style: { color: darkMode ? "#fff" : "#1a1a1a" } }}
            disabled={isGenerating}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#669d2e" },
                "&.Mui-focused fieldset": { borderColor: "#669d2e" },
              },
            }}
          />
          <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>{t("to")}</span>
          <TextField
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => handleDateChange("to", e.target.value)}
            inputProps={{ style: { color: darkMode ? "#fff" : "#1a1a1a" } }}
            disabled={isGenerating}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#669d2e" },
                "&.Mui-focused fieldset": { borderColor: "#669d2e" },
              },
            }}
          />
        </Stack>
        <span
          style={{
            display: "block",
            marginTop: 4,
            fontSize: "0.75rem",
            color: "var(--text-muted)",
          }}
        >
          {activePreset === "allTime"
            ? t("Generating report for all available data")
            : t("Adjust dates or pick a preset above")}
        </span>
      </div>

      {/* sections to include */}
      <div style={{ marginBottom: 16 }}>
        <label
          style={{
            display: "block",
            marginBottom: 8,
            fontWeight: 500,
            fontFamily: "Poppins, sans-serif",
            fontSize: "0.85rem",
            color: "var(--text-primary)",
          }}
        >
          {t("Sections")}
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0px 12px" }}>
          {[
            { key: "trendChart", label: "GHG Trend Chart" },
            { key: "trendTable", label: "GHG Trend Table" },
            { key: "materialChart", label: "Material Chart" },
            { key: "materialTable", label: "Material Table" },
            { key: "receiverTable", label: "Receiver Table" },
          ].map(({ key, label }) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  size="small"
                  checked={sections[key]}
                  onChange={() => toggleSection(key)}
                  disabled={isGenerating}
                  sx={{ color: "#669d2e", "&.Mui-checked": { color: "#669d2e" } }}
                />
              }
              label={t(label)}
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.82rem" } }}
            />
          ))}
        </div>
      </div>

      {/* scope */}
      <div>
        <label
          style={{
            display: "block",
            marginBottom: 8,
            fontWeight: 500,
            fontFamily: "Poppins, sans-serif",
            fontSize: "0.85rem",
            color: "var(--text-primary)",
          }}
        >
          {t("Report Scope")}
        </label>
        <FormControl>
          <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)}>
            <FormControlLabel
              value="all"
              control={<Radio size="small" sx={{ color: "#669d2e", "&.Mui-checked": { color: "#669d2e" } }} />}
              label={t("All receivers")}
              disabled={isGenerating}
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
            />
            <FormControlLabel
              value="specific"
              control={<Radio size="small" sx={{ color: "#669d2e", "&.Mui-checked": { color: "#669d2e" } }} />}
              label={t("Specific receiver")}
              disabled={isGenerating || !uniqueReceivers.length}
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
            />
          </RadioGroup>
        </FormControl>

        {scope === "specific" && (
          <Select
            size="small"
            fullWidth
            value={receiverId}
            onChange={(e) => setReceiverId(e.target.value)}
            displayEmpty
            disabled={isGenerating}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(102,157,46,0.4)" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#669d2e" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#669d2e" },
              "& .MuiSvgIcon-root": { color: "#669d2e" },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  "& .MuiMenuItem-root:hover": { backgroundColor: "rgba(102,157,46,0.12)" },
                  "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "rgba(102,157,46,0.2)" },
                  "& .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: "rgba(102,157,46,0.28)" },
                },
              },
            }}
          >
            <MenuItem value="" disabled>
              {t("Select receiver")}
            </MenuItem>
            {uniqueReceivers.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}{r.location ? ` (${r.location})` : ""}
              </MenuItem>
            ))}
          </Select>
        )}
      </div>
    </BaseDialog>
  );
}
